import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import { formatDistance } from '../../utils/geometry'
import { normalizeAmenityPoiTypeKey } from '../../../Amenities/amenityTranslation'
import { formatHeadwayMinutes } from '../../../Amenities/transitDisplay'

/**
 * Escapes a value before it is interpolated into a tooltip's HTML string.
 *
 * These popups are built as raw HTML and handed to `popup.setHTML()`, and much
 * of what they render is third-party data: POI names come from OpenStreetMap
 * and Overture, which anyone can edit. Without escaping, a POI renamed to an
 * `<img onerror=...>` payload would execute for every user who views a parcel
 * near it.
 */
const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

interface DynamicLayerConfig {
  clustering?: boolean
  clusterMaxZoom?: number
  colors?: string[]
  opacity?: number
  hoveredId?: string | null
  selectedId?: string | null
  strokeColor?: string
  strokeWidth?: number
  lineOnly?: boolean
  lineDasharray?: number[]
}

interface DynamicLayerData {
  id: string
  type: 'markers' | 'polygon'
  geoJson: any
  visible: boolean
  config?: DynamicLayerConfig
}

interface UseDynamicLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  dynamicLayersData?: DynamicLayerData[]
  styleLoadedCount: number
  onDynamicLayerClick?: (properties: any, layerId: string) => void
}

/**
 * Hook to handle dynamic layers rendering (markers, polygons, clusters, tooltips)
 * This is the most complex layer management hook, handling:
 * - Listings markers with price badges
 * - Amenities markers with category colors
 * - Transport stops and infrastructure
 * - Isochrone polygons
 * - Clustering for markers
 * - Interactive tooltips for all types
 */
export const useDynamicLayers = ({
  map,
  mapLoaded,
  popup,
  dynamicLayersData,
  styleLoadedCount,
  onDynamicLayerClick,
}: UseDynamicLayersProps) => {
  const { t } = useTranslation('amenities')

  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Get all currently existing dynamic layers
    let existingDynamicLayers: string[] = []
    let existingDynamicSources: string[] = []

    try {
      const style = map.current.getStyle()
      if (style?.layers) {
        existingDynamicLayers = style.layers
          .filter((layer) => layer.id.startsWith('dynamic-'))
          .map((layer) => layer.id)
      }
      if (style?.sources) {
        existingDynamicSources = Object.keys(style.sources).filter((source) =>
          source.startsWith('dynamic-')
        )
      }
    } catch (error) {
      console.warn('Failed to get existing dynamic layers/sources:', error)
      // Style not loaded yet, skip
      return
    }

    // If no dynamic layers data, remove all existing dynamic layers
    if (!dynamicLayersData || dynamicLayersData.length === 0) {
      // Remove all dynamic layers
      existingDynamicLayers.forEach((layerId) => {
        if (map.current?.getLayer(layerId)) {
          try {
            map.current?.removeLayer(layerId)
          } catch (error) {
            console.warn(`Failed to remove layer ${layerId}:`, error)
          }
        }
      })

      // Remove all dynamic sources
      existingDynamicSources.forEach((sourceId) => {
        if (map.current?.getSource(sourceId)) {
          try {
            map.current?.removeSource(sourceId)
          } catch (error) {
            console.warn(`Failed to remove source ${sourceId}:`, error)
          }
        }
      })

      return
    }

    // Track which layers are currently being rendered
    const currentLayerIds = new Set<string>()
    const currentSourceIds = new Set<string>()

    dynamicLayersData.forEach((layerData) => {
      const { id, type, geoJson, visible, config } = layerData
      const sourceId = `dynamic-${id}-source`
      const layerId = `dynamic-${id}-layer`

      try {
        // Add or update source
        if (!map.current?.getSource(sourceId)) {
          const sourceConfig: any = {
            type: 'geojson',
            data: geoJson,
          }

          // Add clustering if enabled
          if (config?.clustering && type === 'markers') {
            sourceConfig.cluster = true
            sourceConfig.clusterMaxZoom = config.clusterMaxZoom || 14
            sourceConfig.clusterRadius = 50
          }

          map.current?.addSource(sourceId, sourceConfig)
        } else {
          // Update existing source data
          const source = map.current?.getSource(sourceId) as mapboxgl.GeoJSONSource
          source.setData(geoJson)
        }

        // Add layers based on type
        if (type === 'markers') {
          // Add cluster circle layer
          if (config?.clustering && !map.current?.getLayer(`${layerId}-clusters`)) {
            map.current?.addLayer({
              id: `${layerId}-clusters`,
              type: 'circle',
              source: sourceId,
              filter: ['has', 'point_count'],
              layout: {
                visibility: visible ? 'visible' : 'none',
              },
              paint: {
                'circle-color': [
                  'case',
                  ['boolean', ['feature-state', 'selected'], false],
                  '#0066CC', // Blue when selected
                  ['boolean', ['feature-state', 'hovered'], false],
                  '#FF6B00', // Orange when hovered
                  [
                    'step',
                    ['get', 'point_count'],
                    config.colors?.[0] || '#28A745',
                    10,
                    config.colors?.[1] || '#FFC107',
                    30,
                    config.colors?.[2] || '#DC3545',
                  ],
                ],
                'circle-radius': [
                  'case',
                  ['boolean', ['feature-state', 'selected'], false],
                  ['step', ['get', 'point_count'], 24, 10, 36, 30, 48], // Larger when selected
                  ['boolean', ['feature-state', 'hovered'], false],
                  ['step', ['get', 'point_count'], 22, 10, 33, 30, 44], // Slightly larger when hovered
                  ['step', ['get', 'point_count'], 20, 10, 30, 30, 40], // Default size
                ],
              },
            })

            // Add cluster count layer
            map.current?.addLayer({
              id: `${layerId}-cluster-count`,
              type: 'symbol',
              source: sourceId,
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
                visibility: visible ? 'visible' : 'none',
              },
              paint: {
                'text-color': '#ffffff',
              },
            })
          }

          // Add unclustered point layer
          if (!map.current?.getLayer(layerId)) {
            // Check if this is amenities, transport, or building-permits layer for simple circle rendering
            const isSimpleCircleLayer =
              id.startsWith('amenities-') ||
              id.startsWith('transport-') ||
              id === 'building-permits-markers'

            if (isSimpleCircleLayer) {
              // Get the color from config
              const categoryColor = config?.colors?.[0] || '#808080'
              // A transport stop serving more than one mode (an interchange)
              // is drawn once, in the same green used for the multi-modal row
              // in the nearby-stops tab - regardless of which of its layers
              // (bus/tram/train) actually owns the feature, and regardless of
              // which of those layers is toggled on. Keyed off the numeric
              // `modeCount`, never off `modes`: that property is a
              // comma-joined STRING (Mapbox GL returns array properties as JSON
              // strings), and `['length', ...]` on a string counts characters -
              // 'BUS' would score 3 and paint every stop as an interchange.
              const isTransportStopLayer = id.startsWith('transport-') && id.endsWith('-stops')
              const circleColor = isTransportStopLayer
                ? (['case', ['>', ['get', 'modeCount'], 1], '#10b981', categoryColor] as any)
                : categoryColor

              // Build filter: just exclude clusters (source is already filtered by category)
              const layerFilter = config?.clustering ? ['!', ['has', 'point_count']] : undefined

              // Amenities: Simple colored circles based on category
              map.current?.addLayer({
                id: layerId,
                type: 'circle',
                source: sourceId,
                filter: layerFilter,
                layout: {
                  visibility: visible ? 'visible' : 'none',
                },
                paint: {
                  'circle-color': circleColor,
                  'circle-radius':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          14, // Selected size
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          12, // Hovered size
                          10, // Default size
                        ]
                      : 10,
                  'circle-opacity': (config?.opacity || 100) / 100,
                  'circle-stroke-width':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          3, // Selected stroke
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          2, // Hovered stroke
                          0, // Default no stroke
                        ]
                      : 0,
                  'circle-stroke-color': '#ffffff',
                },
              })
            } else {
              // Listings: Price badge style (existing implementation)
              // Add background pill layer
              map.current?.addLayer({
                id: `${layerId}-bg`,
                type: 'circle',
                source: sourceId,
                filter: config?.clustering ? ['!', ['has', 'point_count']] : undefined,
                layout: {
                  visibility: visible ? 'visible' : 'none',
                },
                paint: {
                  'circle-color':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          '#0066CC', // Selected: blue background
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          '#ffffff', // Hovered: white background
                          '#28A745', // Default: green background
                        ]
                      : '#28A745',
                  'circle-radius':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          16, // Selected size
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          14, // Hovered size
                          12, // Default size
                        ]
                      : 12,
                  'circle-opacity': (config?.opacity || 100) / 100,
                },
              })

              // Add price text layer
              map.current?.addLayer({
                id: layerId,
                type: 'symbol',
                source: sourceId,
                filter: config?.clustering ? ['!', ['has', 'point_count']] : undefined,
                layout: {
                  'text-field': ['get', 'priceLabel'],
                  'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
                  'text-size':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          13, // Selected size
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          12, // Hovered size
                          11, // Default size
                        ]
                      : 11,
                  visibility: visible ? 'visible' : 'none',
                  'text-allow-overlap': true,
                  'text-ignore-placement': false,
                },
                paint: {
                  'text-color':
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          '#ffffff', // Selected: white text
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          '#28A745', // Hovered: green text
                          '#ffffff', // Default: white text
                        ]
                      : '#ffffff',
                  'text-opacity': (config?.opacity || 100) / 100,
                },
              })
            }
          } else {
            // Update existing layer paint properties
            const isSimpleCircleLayer =
              id.startsWith('amenities-') ||
              id.startsWith('transport-') ||
              id === 'building-permits-markers'

            if (config?.hoveredId !== undefined || config?.selectedId !== undefined) {
              if (isSimpleCircleLayer) {
                // Update amenities circle layer
                const circleRadius =
                  config?.hoveredId || config?.selectedId
                    ? [
                        'case',
                        ['==', ['get', 'id'], config.selectedId ?? null],
                        14, // Selected size
                        ['==', ['get', 'id'], config.hoveredId ?? null],
                        12, // Hovered size
                        10, // Default size
                      ]
                    : 10

                const circleStrokeWidth =
                  config?.hoveredId || config?.selectedId
                    ? [
                        'case',
                        ['==', ['get', 'id'], config.selectedId ?? null],
                        3, // Selected stroke
                        ['==', ['get', 'id'], config.hoveredId ?? null],
                        2, // Hovered stroke
                        0, // Default no stroke
                      ]
                    : 0

                map.current?.setPaintProperty(layerId, 'circle-radius', circleRadius as any)
                map.current?.setPaintProperty(
                  layerId,
                  'circle-stroke-width',
                  circleStrokeWidth as any
                )
              } else {
                // Update listings price badge layers
                // Update background layer
                const bgLayerId = `${layerId}-bg`
                if (map.current?.getLayer(bgLayerId)) {
                  const bgColor =
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          '#0066CC', // Selected: blue background
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          '#ffffff', // Hovered: white background
                          '#28A745', // Default: green background
                        ]
                      : '#28A745'

                  const bgRadius =
                    config?.hoveredId || config?.selectedId
                      ? [
                          'case',
                          ['==', ['get', 'id'], config.selectedId ?? null],
                          16, // Selected size
                          ['==', ['get', 'id'], config.hoveredId ?? null],
                          14, // Hovered size
                          12, // Default size
                        ]
                      : 12

                  map.current?.setPaintProperty(bgLayerId, 'circle-color', bgColor as any)
                  map.current?.setPaintProperty(bgLayerId, 'circle-radius', bgRadius as any)
                }

                // Update text layer
                const textColor =
                  config?.hoveredId || config?.selectedId
                    ? [
                        'case',
                        ['==', ['get', 'id'], config.selectedId ?? null],
                        '#ffffff', // Selected: white text
                        ['==', ['get', 'id'], config.hoveredId ?? null],
                        '#28A745', // Hovered: green text
                        '#ffffff', // Default: white text
                      ]
                    : '#ffffff'

                const textSize =
                  config?.hoveredId || config?.selectedId
                    ? [
                        'case',
                        ['==', ['get', 'id'], config.selectedId ?? null],
                        13, // Selected size
                        ['==', ['get', 'id'], config.hoveredId ?? null],
                        12, // Hovered size
                        11, // Default size
                      ]
                    : 11

                map.current?.setPaintProperty(layerId, 'text-color', textColor as any)
                map.current?.setLayoutProperty(layerId, 'text-size', textSize as any)
              }
            }

            // Update cluster highlighting when hoveredId or selectedId changes
            if (
              config?.clustering &&
              (config?.hoveredId !== undefined || config?.selectedId !== undefined)
            ) {
              const clusterLayerId = `${layerId}-clusters`
              if (map.current?.getLayer(clusterLayerId)) {
                // Query all visible clusters
                const clusters = map.current?.querySourceFeatures(sourceId, {
                  filter: ['has', 'point_count'],
                })

                // Reset all cluster states first
                clusters.forEach((cluster) => {
                  if (cluster.id !== undefined) {
                    map.current?.setFeatureState(
                      { source: sourceId, id: cluster.id },
                      { selected: false, hovered: false }
                    )
                  }
                })

                // Check each cluster for both selected and hovered states
                const source = map.current?.getSource(sourceId) as mapboxgl.GeoJSONSource

                clusters.forEach((cluster) => {
                  if (cluster.properties?.cluster_id && cluster.id !== undefined) {
                    const clusterId = cluster.properties.cluster_id

                    // Get all leaves (individual markers) in this cluster
                    source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
                      if (!err && leaves && map.current) {
                        // Check if any leaf matches the selected ID
                        const containsSelected =
                          config?.selectedId &&
                          leaves.some((leaf) => leaf.properties?.id === config.selectedId)

                        // Check if any leaf matches the hovered ID
                        const containsHovered =
                          config?.hoveredId &&
                          leaves.some((leaf) => leaf.properties?.id === config.hoveredId)

                        // Set feature states - selected takes precedence
                        map.current.setFeatureState({ source: sourceId, id: cluster.id } as any, {
                          selected: !!containsSelected,
                          hovered: !!containsHovered && !containsSelected,
                        })
                      }
                    })
                  }
                })

                // Update cluster paint property to use feature-state with priority: selected > hovered > default
                const clusterColor = [
                  'case',
                  ['boolean', ['feature-state', 'selected'], false],
                  '#0066CC', // Blue when selected
                  ['boolean', ['feature-state', 'hovered'], false],
                  '#FF6B00', // Orange when hovered
                  [
                    'step',
                    ['get', 'point_count'],
                    config.colors?.[0] || '#28A745',
                    10,
                    config.colors?.[1] || '#FFC107',
                    30,
                    config.colors?.[2] || '#DC3545',
                  ],
                ] as any

                const clusterRadius = [
                  'case',
                  ['boolean', ['feature-state', 'selected'], false],
                  ['step', ['get', 'point_count'], 24, 10, 36, 30, 48], // Larger when selected
                  ['boolean', ['feature-state', 'hovered'], false],
                  ['step', ['get', 'point_count'], 22, 10, 33, 30, 44], // Slightly larger when hovered
                  ['step', ['get', 'point_count'], 20, 10, 30, 30, 40], // Default size
                ] as any

                map.current?.setPaintProperty(clusterLayerId, 'circle-color', clusterColor)
                map.current?.setPaintProperty(clusterLayerId, 'circle-radius', clusterRadius)
              }
            }
          }
        } else if (type === 'polygon') {
          // Handle polygon layers (isochrones)
          // Each GeoJSON may contain multiple features (one per time zone)
          const features = geoJson.features || []

          features.forEach((feature: any, index: number) => {
            const fillLayerId = `${layerId}-${index}`
            // Support both 'minutes' (transport isochrones) and 'contour' (demographics isochrones)
            const minutes = feature.properties?.minutes
            const contour = feature.properties?.contour
            const color = feature.properties?.color || config?.colors?.[index] || '#808080'

            if (!map.current?.getLayer(fillLayerId)) {
              // Build filter based on available properties
              // If only one feature or no time property, don't filter
              let filter: any = undefined
              if (features.length > 1) {
                if (minutes !== undefined) {
                  filter = ['==', ['get', 'minutes'], minutes]
                } else if (contour !== undefined) {
                  filter = ['==', ['get', 'contour'], contour]
                }
              }

              // Check if this should be rendered as line only (dashed border)
              if (config?.lineOnly) {
                const lineLayerConfig: any = {
                  id: fillLayerId,
                  type: 'line',
                  source: sourceId,
                  layout: {
                    visibility: visible ? 'visible' : 'none',
                  },
                  paint: {
                    'line-color': config?.strokeColor || color,
                    'line-width': config?.strokeWidth || 2,
                    'line-opacity': (config?.opacity || 100) / 100,
                    'line-dasharray': config?.lineDasharray || [4, 4],
                  },
                }

                // Only add filter if we have one
                if (filter) {
                  lineLayerConfig.filter = filter
                }

                map.current?.addLayer(lineLayerConfig)
              } else {
                // Standard fill layer
                const layerConfig: any = {
                  id: fillLayerId,
                  type: 'fill',
                  source: sourceId,
                  layout: {
                    visibility: visible ? 'visible' : 'none',
                  },
                  paint: {
                    'fill-color': color,
                    'fill-opacity': (config?.opacity || 30) / 100,
                  },
                }

                // Only add filter if we have one
                if (filter) {
                  layerConfig.filter = filter
                }

                map.current?.addLayer(layerConfig)
              }
            } else {
              // Update existing polygon layer opacity
              if (config?.lineOnly) {
                map.current?.setPaintProperty(
                  fillLayerId,
                  'line-opacity',
                  (config?.opacity || 100) / 100
                )
              } else {
                map.current?.setPaintProperty(
                  fillLayerId,
                  'fill-opacity',
                  (config?.opacity || 30) / 100
                )
              }
            }
          })
        }

        // Update visibility for all related layers
        // Determine which layers exist based on layer type and rendering style
        const isSimpleCircleLayer =
          id.startsWith('amenities-') ||
          id.startsWith('transport-') ||
          id === 'building-permits-markers'
        const hasBackgroundLayer = !isSimpleCircleLayer // Only listings have -bg layer

        const layerIds =
          type === 'polygon'
            ? // For polygons, collect all feature-based layer IDs
              (geoJson.features || []).map((_: any, index: number) => `${layerId}-${index}`)
            : config?.clustering
              ? // Clustered layers: include clusters and cluster-count, bg only for listings
                hasBackgroundLayer
                ? [layerId, `${layerId}-bg`, `${layerId}-clusters`, `${layerId}-cluster-count`]
                : [layerId, `${layerId}-clusters`, `${layerId}-cluster-count`]
              : // Non-clustered layers: include bg only for listings
                hasBackgroundLayer
                ? [layerId, `${layerId}-bg`]
                : [layerId]

        layerIds.forEach((lid: any) => {
          if (map.current?.getLayer(lid)) {
            map.current?.setLayoutProperty(lid, 'visibility', visible ? 'visible' : 'none')
          }
        })

        // Add click handler for unclustered markers
        if (onDynamicLayerClick && type === 'markers') {
          // For listings/transactions the visible pill is two stacked
          // layers: the `${layerId}-bg` circle (background) and the
          // `layerId` text symbol on top. Clicks register only inside
          // the symbol's tight glyph bounding box otherwise — bind the
          // listener to both layers so the whole pill is hit-testable.
          const bgLayerId = `${layerId}-bg`
          const clickTargets = map.current?.getLayer(bgLayerId) ? [bgLayerId, layerId] : layerId

          // Add click handler
          map.current?.on('click', clickTargets as any, (e: mapboxgl.MapLayerMouseEvent) => {
            if (e.features && e.features.length > 0) {
              const feature = e.features[0]
              onDynamicLayerClick(feature.properties, id)
            }
          })

          // Add cursor pointer on hover (both bg and label, when split)
          map.current?.on('mouseenter', clickTargets as any, () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = 'pointer'
            }
          })

          map.current?.on('mouseleave', clickTargets as any, () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = ''
              // Hide tooltip on mouse leave
              if (popup.current) {
                popup.current.remove()
              }
            }
          })

          // Add tooltip on hover for listings-markers layer
          if (id === 'listings-markers') {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0 || !map.current || !popup.current) return

              const feature = e.features[0]
              const coordinates = (feature.geometry as any).coordinates.slice()
              const properties = feature.properties

              // Ensure that if the map is zoomed out such that multiple
              // copies of the feature are visible, the popup appears
              // over the copy being pointed to.
              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              // Create HTML for the tooltip
              const imageUrl =
                properties?.urls_image ||
                (properties?.hash_images
                  ? `https://images.popety.io/${encodeURIComponent(String(properties?.hash_images ?? ''))}`
                  : null)

              const formatPrice = (price: number) => {
                if (!price) return 'Price on request'
                return new Intl.NumberFormat('en-CH', {
                  style: 'currency',
                  currency: 'CHF',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(price)
              }

              const tooltipHTML = `
                <div style="min-width: 200px; max-width: 280px; background: #ffffff; border-radius: 8px; overflow: hidden;">
                  ${
                    imageUrl
                      ? `<img src="${imageUrl}" alt="Listing" style="width: 100%; height: 80px; object-fit: cover;" />`
                      : `<div style="width: 100%; height: 80px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #999; font-size: 11px;">No image</div>`
                  }
                  <div style="padding: 10px 12px;">
                    <div style="font-size: 16px; font-weight: 700; color: #0294df; margin-bottom: 6px;">
                      ${formatPrice(properties?.price)}
                    </div>
                    <div style="font-size: 12px; color: #6b6b6b; line-height: 1.4; margin-bottom: 6px;">
                      ${properties?.rooms && properties?.area ? `<div style="margin-bottom: 2px;">${properties?.rooms} rooms • ${properties?.area} m²</div>` : ''}
                      ${!properties?.rooms && properties?.area ? `<div style="margin-bottom: 2px;">${properties?.area} m²</div>` : ''}
                      ${properties?.rooms && !properties?.area ? `<div style="margin-bottom: 2px;">${properties?.rooms} rooms</div>` : ''}
                      ${properties?.city ? `<div>${escapeHtml(properties?.city)}</div>` : ''}
                    </div>
                    <div style="display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px;">
                      ${properties?.propertyType ? `<span style="display: inline-block; padding: 2px 6px; background: rgba(2, 148, 223, 0.1); color: #0294df; border-radius: 4px; font-size: 10px; font-weight: 500;">${escapeHtml(properties?.propertyType)}</span>` : ''}
                      ${properties?.dealType ? `<span style="display: inline-block; padding: 2px 6px; background: rgba(0, 179, 65, 0.1); color: #00b341; border-radius: 4px; font-size: 10px; font-weight: 500;">${properties?.dealType === 'rent' ? 'For Rent' : 'For Sale'}</span>` : ''}
                    </div>
                  </div>
                </div>
              `

              // Set popup location and content
              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for amenities markers
          if (id.startsWith('amenities-')) {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0 || !map.current || !popup.current) return

              const feature = e.features[0]
              const coordinates = (feature.geometry as any).coordinates.slice()
              const properties = feature.properties

              // Ensure that if the map is zoomed out such that multiple
              // copies of the feature are visible, the popup appears
              // over the copy being pointed to.
              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              // Category colors
              const categoryColors: Record<string, string> = {
                shopping: '#ff2d55',
                grocery: '#00b341',
                culture: '#af52de',
                dining: '#ff3b30',
                education: '#ff9500',
                wellness: '#ff69b4',
                healthcare: '#7b61ff',
                transport: '#0294df',
                services: '#8e8e93',
              }

              const category = properties?.category
              const categoryColor = categoryColors[category] || '#808080'

              /*
                  Facilities such as pitches and recycling points have no name
                  in OSM, and the stored blob wrote the literal "Unknown" for
                  them. Show the type instead — it is the identity — and keep
                  the two lines from repeating: a named place gets its type
                  underneath, an unnamed one gets its category.
                */
              const rawName = properties?.name
              const isUnnamed = !rawName || rawName === 'Unknown'
              const typeLabel = properties?.type
                ? t(`poiTypes.${normalizeAmenityPoiTypeKey(properties.type)}`, properties.type)
                : ''
              const categoryLabel = t(`categories.${category}`, category)

              /*
                  A transport stop carries every mode calling at it. Without
                  this the popup showed the single poi_type - "Tram Stop" for a
                  stop the list beside it calls "Tram · Bus". Same keys as the
                  Amenities rows and the Transport tab's own map, so all three
                  surfaces read the same words.

                  `modes` is a comma-joined STRING: Mapbox GL returns array
                  properties as JSON strings.
                */
              const modeCodes: string[] =
                typeof properties?.modes === 'string' && properties.modes
                  ? properties.modes.split(',').filter(Boolean)
                  : []
              const modesLabel = modeCodes
                .map((mode) => t(`transitModes.${mode}`, mode))
                .join(' · ')

              const headwayMin = properties?.headwayMin
              const intervalMinutes =
                typeof headwayMin === 'number' ? formatHeadwayMinutes(headwayMin) : null
              const intervalLabel =
                intervalMinutes === null
                  ? null
                  : t('categoryCard.everyMinutes', {
                      count: Number(intervalMinutes),
                      minutes: intervalMinutes,
                      defaultValue:
                        intervalMinutes === '1' ? 'every minute' : `every ${intervalMinutes} min`,
                    })

              const transitLabel = [modesLabel, intervalLabel].filter(Boolean).join(' · ')

              const title = isUnnamed ? typeLabel || categoryLabel : rawName
              const subtitle = isUnnamed
                ? transitLabel || categoryLabel
                : transitLabel || typeLabel || categoryLabel

              const tooltipHTML = `
                <div style="min-width: 180px; max-width: 240px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                  <div style="padding: 12px;">
                    <div style="font-size: 14px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.3;">
                      ${escapeHtml(title)}
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b6b6b;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${categoryColor};"></span>
                        <span>${escapeHtml(subtitle)}</span>
                      </div>
                      ${
                        properties?.distance
                          ? `
                        <div style="display: flex; align-items: center; gap: 6px;">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="4" cy="4" r="3" stroke="#6b6b6b" stroke-width="1"/>
                          </svg>
                          <span>${t('map.distanceAway', {
                            distance: formatDistance(properties?.distance),
                            defaultValue: `${formatDistance(properties?.distance)} away`,
                          })}</span>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  </div>
                </div>
              `

              // Set popup location and content
              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for transport stops (bus, tram, train)
          if (id.startsWith('transport-') && id.endsWith('-stops')) {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0 || !map.current || !popup.current) return

              const feature = e.features[0]
              const coordinates = (feature.geometry as any).coordinates.slice()
              const properties = feature.properties

              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              // The stop's full mode list, not just the layer it happened to
              // be drawn in - a bus+tram interchange drawn by the tram layer
              // must still read "Tram · Bus", not "Tram". Falls back to the
              // layer's own bucket label for payloads with no modes array
              // (e.g. legacy GeoAdmin stops).
              const stopType = properties?.stopType
              const stopColors: Record<string, string> = {
                bus: '#f59e0b',
                tram: '#3b82f6',
                train: '#ef4444',
              }
              const stopIcons: Record<string, string> = {
                bus: t('transitModes.BUS', 'Bus'),
                tram: t('transitModes.TRAM', 'Tram'),
                train: t('transitModes.TRAIN', 'Train'),
              }
              /*
                  Mapbox GL carries only primitives through its rendering
                  pipeline, so `modes` is written as a comma-joined string of
                  ATLAS codes rather than an array - an array would arrive here
                  as a JSON string and silently fail every check.
                */
              const modes: string[] =
                typeof properties?.modes === 'string' && properties.modes
                  ? properties.modes.split(',').filter(Boolean)
                  : []
              const isInterchange = modes.length > 1
              const stopColor = isInterchange ? '#10b981' : stopColors[stopType] || '#808080'
              const modesLabel =
                modes.length > 0
                  ? modes.map((mode) => escapeHtml(t(`transitModes.${mode}`, mode))).join(' · ')
                  : escapeHtml(stopIcons[stopType] || stopType)

              const headwayMin = properties?.headwayMin
              const intervalMinutes =
                typeof headwayMin === 'number' ? formatHeadwayMinutes(headwayMin) : null
              const intervalLabel =
                intervalMinutes === null
                  ? null
                  : t('categoryCard.everyMinutes', {
                      count: Number(intervalMinutes),
                      minutes: intervalMinutes,
                      defaultValue:
                        intervalMinutes === '1' ? 'every minute' : `every ${intervalMinutes} min`,
                    })

              const tooltipHTML = `
                <div style="min-width: 180px; max-width: 240px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                  <div style="padding: 12px;">
                    <div style="font-size: 14px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.3;">
                      ${escapeHtml(
                        properties?.name ||
                          t('publicTransport.unknownStop', {
                            ns: 'transport',
                            defaultValue: 'Unknown Stop',
                          })
                      )}
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b6b6b;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${stopColor};"></span>
                        <span>${modesLabel}</span>
                      </div>
                      ${
                        intervalLabel
                          ? `
                        <div>
                          ${escapeHtml(intervalLabel)}
                        </div>
                      `
                          : ''
                      }
                      ${
                        properties?.operator
                          ? `
                        <div style="color: #9ca3af;">
                          ${escapeHtml(properties?.operator)}
                        </div>
                      `
                          : ''
                      }
                      ${
                        properties?.distance
                          ? `
                        <div style="display: flex; align-items: center; gap: 6px; margin-top: 4px;">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4" cy="4" r="3" stroke="#6b6b6b" stroke-width="1"/>
                          </svg>
                          <span>${escapeHtml(
                            t('map.distanceAway', {
                              distance: formatDistance(properties?.distance),
                              defaultValue: `${formatDistance(properties?.distance)} away`,
                            })
                          )}</span>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  </div>
                </div>
              `

              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for EV charging stations
          if (id === 'transport-ev-charging') {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0 || !map.current || !popup.current) return

              const feature = e.features[0]
              const coordinates = (feature.geometry as any).coordinates.slice()
              const properties = feature.properties

              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              const chargingType = properties?.chargingType
              const typeLabels: Record<string, string> = {
                fast: 'Fast Charging',
                medium: 'Medium Charging',
                standard: 'Standard Charging',
              }
              const typeLabel = typeLabels[chargingType] || 'Charging Station'

              const tooltipHTML = `
                <div style="min-width: 180px; max-width: 260px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                  <div style="padding: 12px;">
                    <div style="font-size: 14px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.3;">
                      ${escapeHtml(properties?.address || 'EV Charging Station')}
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b6b6b;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #8b5cf6;"></span>
                        <span>${typeLabel}</span>
                      </div>
                      <div style="color: #9ca3af;">
                        ${properties?.chargerCount || 0} charger${properties?.chargerCount !== 1 ? 's' : ''} • ${properties?.minPower === properties?.maxPower ? properties?.maxPower : `${properties?.minPower}-${properties?.maxPower}`} kW
                      </div>
                      ${
                        properties?.operator
                          ? `
                        <div style="color: #9ca3af;">
                          ${escapeHtml(properties?.operator)}
                        </div>
                      `
                          : ''
                      }
                      ${
                        properties?.distance
                          ? `
                        <div style="display: flex; align-items: center; gap: 6px; margin-top: 4px;">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4" cy="4" r="3" stroke="#6b6b6b" stroke-width="1"/>
                          </svg>
                          <span>${formatDistance(properties?.distance)} away</span>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  </div>
                </div>
              `

              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for highway access points
          if (id === 'transport-highway-access') {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0 || !map.current || !popup.current) return

              const feature = e.features[0]
              const coordinates = (feature.geometry as any).coordinates.slice()
              const properties = feature.properties

              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              const tooltipHTML = `
                <div style="min-width: 180px; max-width: 240px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                  <div style="padding: 12px;">
                    <div style="font-size: 14px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.3;">
                      ${escapeHtml(properties?.name || 'Highway Access')}
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b6b6b;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #10b981;"></span>
                        <span>Highway Exit/Entrance</span>
                      </div>
                      ${
                        properties?.roadNumber
                          ? `
                        <div style="color: #9ca3af;">
                          ${escapeHtml(properties?.roadNumber)}
                        </div>
                      `
                          : ''
                      }
                      ${
                        properties?.distance
                          ? `
                        <div style="display: flex; align-items: center; gap: 6px; margin-top: 4px;">
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <circle cx="4" cy="4" r="3" stroke="#6b6b6b" stroke-width="1"/>
                          </svg>
                          <span>${formatDistance(properties?.distance)} away</span>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  </div>
                </div>
              `

              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for building permits markers
          if (id === 'building-permits-markers') {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0) return

              const feature = e.features[0]
              const properties = feature.properties

              const coordinates = (feature.geometry as any).coordinates?.slice()
              if (!coordinates) return

              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              // Format date for display
              const formatPermitDate = (dateStr: string | null) => {
                if (!dateStr) return null
                return new Date(dateStr).toLocaleDateString('fr-CH')
              }

              // Map status to display labels
              const statusLabels: Record<string, string> = {
                approved: 'Approved',
                pending: 'Pending',
                rejected: 'Rejected',
                completed: 'Completed',
                inquiry: 'In Inquiry',
              }
              const statusColors: Record<string, string> = {
                approved: '#22c55e',
                pending: '#f59e0b',
                rejected: '#ef4444',
                completed: '#3b82f6',
                inquiry: '#8b5cf6',
              }

              const status = properties?.status?.toLowerCase() || 'pending'
              const statusLabel = statusLabels[status] || properties?.status || 'Unknown'
              const statusColor = statusColors[status] || '#8b5cf6'

              const tooltipHTML = `
                  <div style="min-width: 200px; max-width: 280px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <div style="background: #8b5cf6; padding: 8px 12px;">
                      <div style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px;">
                        Building Permit
                      </div>
                    </div>
                    <div style="padding: 12px;">
                      <div style="font-size: 13px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.4;">
                        ${escapeHtml(properties?.description || 'No description')}
                      </div>
                      <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;">
                        <span style="display: inline-block; padding: 2px 8px; background: ${statusColor}20; color: ${statusColor}; border-radius: 4px; font-size: 10px; font-weight: 600;">
                          ${statusLabel}
                        </span>
                        ${
                          properties?.category
                            ? `
                          <span style="display: inline-block; padding: 2px 8px; background: #f3f4f6; color: #6b7280; border-radius: 4px; font-size: 10px; font-weight: 500;">
                            ${escapeHtml(properties.category)}
                          </span>
                        `
                            : ''
                        }
                      </div>
                      <div style="font-size: 11px; color: #6b7280; line-height: 1.5;">
                        ${
                          properties?.cityName
                            ? `<div style="margin-bottom: 2px;">📍 ${escapeHtml(properties.cityName)}${properties.postalCodes ? ` (${escapeHtml(properties.postalCodes)})` : ''}</div>`
                            : ''
                        }
                        ${
                          properties?.inquiryStartDate
                            ? `<div>📅 ${formatPermitDate(properties.inquiryStartDate)}</div>`
                            : ''
                        }
                        ${
                          properties?.primaryOfficialId
                            ? `<div style="margin-top: 4px; font-size: 10px; color: #9ca3af;">ID: ${escapeHtml(properties.primaryOfficialId)}</div>`
                            : ''
                        }
                      </div>
                    </div>
                  </div>
                `

              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })
          }

          // Add tooltip on hover for transaction points
          if (id === 'transaction-points') {
            map.current?.on('mousemove', layerId, (e: mapboxgl.MapLayerMouseEvent) => {
              if (!e.features || e.features.length === 0) return

              const feature = e.features[0]
              const properties = feature.properties

              const coordinates = (feature.geometry as any).coordinates?.slice()
              if (!coordinates) return

              while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
              }

              // Format date for display
              const formatDate = (dateStr: string | null) => {
                if (!dateStr) return null
                return new Date(dateStr).toLocaleDateString('fr-CH')
              }

              // Format price for display with Swiss apostrophe separator
              const formatPrice = (price: number | string | null) => {
                if (!price) return null
                const numPrice = typeof price === 'string' ? parseFloat(price) : price
                if (isNaN(numPrice)) return null
                // Format with Swiss apostrophe separator
                const formatted = Math.round(numPrice)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, "'")
                return `${formatted} CHF`
              }

              // Lucide SVG icons (16x16)
              const mapPinIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px;"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>`
              const calendarIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px;"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg>`
              const rulerIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px;"><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z"/><path d="m14.5 12.5 2-2"/><path d="m11.5 9.5 2-2"/><path d="m8.5 6.5 2-2"/><path d="m17.5 15.5 2-2"/></svg>`

              const reason = properties?.reason || 'Transaction'

              const tooltipHTML = `
                  <div style="min-width: 180px; max-width: 260px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                    <div style="background: #10b981; padding: 8px 12px;">
                      <div style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px;">
                        ${reason}
                      </div>
                    </div>
                    <div style="padding: 12px;">
                      ${
                        properties?.price
                          ? `
                        <div style="font-size: 16px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px;">
                          ${formatPrice(properties.price)}
                        </div>
                      `
                          : ''
                      }
                      <div style="font-size: 11px; color: #6b7280; line-height: 1.5;">
                        ${properties?.city ? `<div style="display: flex; align-items: center; margin-bottom: 4px;">${mapPinIcon} ${escapeHtml(properties.city)}</div>` : ''}
                        ${properties?.date ? `<div style="display: flex; align-items: center; margin-bottom: 4px;">${calendarIcon} ${formatDate(properties.date)}</div>` : ''}
                        ${properties?.area ? `<div style="display: flex; align-items: center; margin-bottom: 4px;">${rulerIcon} ${properties.area} m²</div>` : ''}
                        ${properties?.propertyType ? `<div style="margin-top: 4px; font-size: 10px; color: #9ca3af;">${escapeHtml(properties.propertyType)}</div>` : ''}
                      </div>
                    </div>
                  </div>
                `

              popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
            })

            map.current?.on('mouseleave', layerId, () => {
              popup.current.remove()
            })
          }
        }

        // Add click handler for clusters to zoom in
        if (config?.clustering) {
          const clusterLayerId = `${layerId}-clusters`
          const handleClusterClick = () => {}
          // Remove existing click handler
          map.current?.off('click', clusterLayerId, handleClusterClick as any)

          // Add click handler to zoom into cluster
          map.current?.on('click', clusterLayerId, (e: mapboxgl.MapLayerMouseEvent) => {
            if (!e.features || e.features.length === 0) return

            const clusterId = e.features[0].properties?.cluster_id
            const source = map.current?.getSource(sourceId) as mapboxgl.GeoJSONSource

            source.getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err || !map.current) return

              map.current.easeTo({
                center: (e?.features?.[0]?.geometry as any)?.coordinates,
                zoom,
              } as any)
            })
          })

          // Add cursor pointer on cluster hover
          map.current?.on('mouseenter', clusterLayerId, () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = 'pointer'
            }
          })

          map.current?.on('mouseleave', clusterLayerId, () => {
            if (map.current) {
              map.current.getCanvas().style.cursor = ''
            }
          })
        }

        // Track this layer and source as current
        currentSourceIds.add(sourceId)

        if (type === 'polygon') {
          // Track all polygon feature layers
          const features = geoJson.features || []
          features.forEach((_: any, index: number) => {
            currentLayerIds.add(`${layerId}-${index}`)
          })
        } else {
          // Track marker layers
          currentLayerIds.add(layerId)
          currentLayerIds.add(`${layerId}-bg`)
          if (config?.clustering) {
            currentLayerIds.add(`${layerId}-clusters`)
            currentLayerIds.add(`${layerId}-cluster-count`)
          }
        }
      } catch (error) {
        console.warn(`Failed to add/update dynamic layer ${id}:`, error)
      }
    })

    // Remove layers that are no longer in dynamicLayersData
    existingDynamicLayers.forEach((layerId) => {
      if (!currentLayerIds.has(layerId)) {
        if (map.current?.getLayer(layerId)) {
          try {
            map.current?.removeLayer(layerId)
          } catch (error) {
            console.warn(`Failed to remove layer ${layerId}:`, error)
          }
        }
      }
    })

    // Remove sources that are no longer in dynamicLayersData
    existingDynamicSources.forEach((sourceId) => {
      if (!currentSourceIds.has(sourceId)) {
        if (map.current?.getSource(sourceId)) {
          try {
            map.current?.removeSource(sourceId)
          } catch (error) {
            console.warn(`Failed to remove source ${sourceId}:`, error)
          }
        }
      }
    })
    // `t` is a dependency so tooltips re-bind when the user switches language.
  }, [dynamicLayersData, mapLoaded, styleLoadedCount, onDynamicLayerClick, t])
}
